import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/Button';
import { Car as CarIcon, Plus, ChevronLeft } from '../components/Icon';
import { useAuth } from '../contexts/AuthContext';
import { useDocumentHead } from '../lib/seo';
import clsx from 'clsx';

export default function GaragePage() {
  const navigate = useNavigate();
  const { profile, saveCars, firebaseUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useDocumentHead({
    title: 'My garage · ParkProof',
    description: 'Manage your saved cars on ParkProof.',
    noindex: true,
  });

  if (!firebaseUser) {
    // Soft gate — show prompt to sign in.
    return (
      <div className="p-s5 pt-s8 flex flex-col gap-s5">
        <div className="caption text-ink3 font-mono">MY GARAGE</div>
        <h1 className="font-serif text-[30px] leading-[33px] tracking-tight text-ink1">
          Sign in to <span className="font-serif-italic">save cars</span>
        </h1>
        <p className="text-[14px] text-ink2 font-sans leading-snug">
          Your garage is tied to your account. Sign in to add cars and pick what you're driving.
        </p>
        <Button block onClick={() => navigate('/signin')}>
          Sign in or create account
        </Button>
      </div>
    );
  }

  const cars = profile?.cars ?? [];

  const makeActive = async (index: number) => {
    if (index === 0 || busy) return;
    setBusy(true);
    setError(null);
    try {
      const reordered = [cars[index], ...cars.filter((_, i) => i !== index)];
      await saveCars(reordered);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const deleteCar = async (index: number) => {
    if (busy) return;
    const ok = window.confirm(`Remove "${cars[index].label}" from your garage?`);
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      await saveCars(cars.filter((_, i) => i !== index));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col">
      <div className="px-s4 py-s2">
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 flex items-center justify-center"
          aria-label="Back"
        >
          <ChevronLeft size={20} />
        </button>
      </div>

      <div className="p-s5 flex flex-col gap-s5">
        <div>
          <div className="caption text-ink3 mb-s3 font-mono">MY GARAGE</div>
          <h1 className="font-serif text-[30px] leading-[33px] tracking-tight text-ink1">
            Your <span className="font-serif-italic">cars</span>
          </h1>
          <p className="mt-1.5 text-[14px] text-ink2 font-sans leading-snug">
            Tap a car to make it your active vehicle. The first car in the list is used for all
            height checks.
          </p>
        </div>

        {cars.length === 0 ? (
          <div className="mt-s5 flex flex-col items-center gap-s3 py-s5">
            <CarIcon size={36} color="#8B8680" />
            <span className="text-[13px] text-ink3 font-sans">No cars yet — add one below.</span>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {cars.map((car, i) => {
              const isActive = i === 0;
              return (
                <button
                  type="button"
                  key={car.id}
                  onClick={() => makeActive(i)}
                  disabled={busy || isActive}
                  className={clsx(
                    'flex items-center gap-s4 p-s4 border rounded-md text-left bg-card transition-colors',
                    isActive ? 'border-ink1 bg-tint' : 'border-rule hover:bg-tint'
                  )}
                >
                  <div
                    className={clsx(
                      'w-11 h-11 rounded-pill flex items-center justify-center border',
                      isActive
                        ? 'bg-ink1 border-ink1'
                        : 'bg-page border-rule'
                    )}
                  >
                    <CarIcon size={22} color={isActive ? '#FFFFFF' : '#4D4B47'} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-s2 mb-0.5">
                      <span className="font-sans font-semibold text-[15px] text-ink1 truncate">
                        {car.label}
                      </span>
                      {isActive ? (
                        <span className="px-2 py-0.5 bg-ink1 rounded-pill text-[10px] font-sans font-semibold text-card tracking-wide">
                          Driving now
                        </span>
                      ) : null}
                    </div>
                    <div className="font-mono text-[11px] text-ink3 tracking-wide">
                      {car.type.toUpperCase()} · {car.heightM.toFixed(2)} M
                      {car.hasRoofRack ? ' · ROOF RACK' : ''}
                      {car.hasRoofBox ? ' · ROOF BOX' : ''}
                    </div>
                  </div>
                  {cars.length > 1 ? (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteCar(i);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.stopPropagation();
                          deleteCar(i);
                        }
                      }}
                      className="w-7 h-7 flex items-center justify-center text-[13px] text-ink3 hover:text-ink1"
                    >
                      ✕
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        )}

        {error ? <div className="text-[13px] text-tier-reported font-sans">{error}</div> : null}

        <Link to="/garage/add" className="block">
          <Button block variant="secondary" icon={<Plus size={14} color="#0F0F0E" />}>
            Add a car
          </Button>
        </Link>
      </div>
    </div>
  );
}
